import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtMoney, fmtDate, fmtDateTime, useIsMobile } from './UI';

// RentalMan invoices behind an opportunity's Actual $ (George 2026-09-14,
// docs/SPEC-rm-invoiced-actuals.md section 8). The list comes from the SAME
// matcher the nightly recompute runs (services/rmActuals.js
// loadProjectInvoices), so the counted rows always add up to the figure on the
// row that opened it. Two callers, two routes: the Project Tracker reads
// through the sales scope, the Operations Active Jobs table through the Ops
// router (ops is denied the /api/projects routes).

// rm_flags is JSONB and node-pg hands it over parsed; a string is tolerated so
// a serializer change can never blank the disclosures.
export const rmFlagsOf = (v) => {
  if (!v) return {};
  if (typeof v === 'string') {
    try { return JSON.parse(v) || {}; } catch { return {}; }
  }
  return v;
};

// Cents on purpose: these are invoice records and the spec figures reconcile
// to the cent. The table cells elsewhere stay whole dollars via fmtMoney.
const money2 = (v) => {
  const n = Number(v || 0);
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (n < 0 ? '-$' : '$') + s;
};

// held_reason set = the invoice named this job AND another opportunity, so it
// counts toward neither. `match` is still filled on those rows (how the PO
// field named the job), which is why held_reason is checked first.
const matchLabel = (inv) => {
  if (inv.held_reason === 'shared_po') return 'Held out: shared PO';
  if (inv.held_reason === 'multi_opportunity') return 'Held out: several opportunities';
  if (inv.held_reason) return 'Held out';
  if (inv.match === 'multi_po') return 'Multi-PO';
  return inv.match === 'suffix' ? 'PO + RM suffix' : 'Exact';
};

// The Actual $ cell shared by the Project Tracker (table + mobile card) and
// the Operations Active Jobs table, so the two screens can never render the
// figure differently. The modal itself is owned by the caller: rendered inside
// a row, a fixed overlay would inherit the row's opacity (hidden ops jobs).
//   project: a GET /api/projects or /api/operations/feed row
//   onOpen(project): open RmInvoicesModal for this row
//   empty: what to print when there is no Actual at all
//   suffix: inline node right after the figure (the mobile card's variance)
export function RmActualCell({ project: p, onOpen, empty = '', suffix = null }) {
  const flags = rmFlagsOf(p.rm_flags);
  const heldAmount = Number(flags.held_out_amount) || 0;
  const heldCount = Number(flags.held_out_count) || 0;
  const isRm = p.actual_source === 'rm';
  // Width caps are MEASURED against the Operations table (see the layout note
  // there): a note wider than the figure raises that column's share of the
  // free space and squeezes Site and Equipment.
  const note = { fontSize: 10, marginTop: 2, lineHeight: 1.25, maxWidth: 90, overflowWrap: 'anywhere' };
  const linkBtn = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit',
    textAlign: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2,
  };
  return (
    <>
      {/* One span so a column-flex parent (the mobile card) keeps the figure
          and its suffix on one line. */}
      <span>{p.actual_value != null ? fmtMoney(p.actual_value) : empty}{suffix}</span>
      {isRm && (
        <div style={{ marginTop: 2, lineHeight: 1.2 }}>
          {onOpen ? (
            <button
              type="button"
              onClick={() => onOpen(p)}
              title="Actual $ is the ex-tax total of the RentalMan invoices billed to this PO. Click for the invoice list."
              style={{ ...linkBtn, fontSize: 10, fontWeight: 700, color: colors.blue, whiteSpace: 'nowrap' }}
            >
              RM · {p.rm_invoice_count ?? 0} inv
            </button>
          ) : (
            <span style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, whiteSpace: 'nowrap' }}>
              RM · {p.rm_invoice_count ?? 0} inv
            </span>
          )}
        </div>
      )}
      {flags.no_invoices && (
        <div style={{ ...note, color: colors.gray }}>No RentalMan invoices yet</div>
      )}
      {heldAmount > 0 && (
        onOpen ? (
          <div style={note}>
            <button
              type="button"
              onClick={() => onOpen(p)}
              title={`${heldCount || 'Some'} invoice${heldCount === 1 ? '' : 's'} name this job together with another opportunity (a shared PO, or one invoice listed under two RentalMan accounts with a different PO on each), so they count toward neither. Click for the list.`}
              style={{ ...linkBtn, color: '#B45309', overflowWrap: 'anywhere' }}
            >
              {fmtMoney(heldAmount)} held out (shared)
            </button>
          </div>
        ) : (
          <div style={{ ...note, color: '#B45309' }}>{fmtMoney(heldAmount)} held out (shared)</div>
        )
      )}
    </>
  );
}

export default function RmInvoicesModal({ projectId, source = 'sales', onClose, title }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;
    setData(null); setError('');
    const load = source === 'ops' ? api.opsProjectRmInvoices : api.projectRmInvoices;
    load(projectId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [projectId, source]);

  const invoices = Array.isArray(data?.invoices) ? data.invoices : [];
  // Newest first for reading. The server's order is what keeps the collapsed
  // flags stable; sorting a copy here changes only the display.
  const rows = [...invoices].sort((a, b) => {
    const da = String(a.invoice_date || ''), db = String(b.invoice_date || '');
    if (da !== db) return da < db ? 1 : -1;
    if (String(a.invoice_no) !== String(b.invoice_no)) return String(a.invoice_no) < String(b.invoice_no) ? 1 : -1;
    return (Number(b.invoice_seq) || 0) - (Number(a.invoice_seq) || 0);
  });
  const counted = invoices.filter(i => !i.held_reason);
  const multiPo = counted.filter(i => i.match === 'multi_po' && !i.collapsed).length;
  const suffixed = counted.filter(i => i.match === 'suffix' && !i.collapsed).length;
  const collapsedCount = counted.filter(i => i.collapsed).length;
  const customers = new Set(counted.map(i => String(i.customer_name || '').trim() || `#${i.account_no}`));
  const poKeys = Array.isArray(data?.po_keys) ? data.po_keys : [];
  const placeholderKeys = Array.isArray(data?.placeholder_keys) ? data.placeholder_keys : [];
  const heldCount = Number(data?.held_out_count) || 0;
  const invoiceCount = Number(data?.invoice_count) || 0;

  const td = { ...styles.td, padding: '7px 8px' };
  const th = { ...styles.th, padding: '8px 8px' };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, zIndex: 1000
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 10, width: '100%', maxWidth: 1100,
          maxHeight: '90vh',
          // Mobile: the whole modal scrolls, same as the Billed drill-down and
          // the Job Hub (a pinned header starves the body on zoomed phones).
          ...(isMobile
            ? { overflowY: 'auto', WebkitOverflowScrolling: 'touch' }
            : { display: 'flex', flexDirection: 'column' }),
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
        }}
      >
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ ...styles.h2, margin: 0, fontSize: 18 }}>{title || 'RentalMan invoices'}</h2>
            {data && (
              <>
                <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
                  {/* in_scope false: not an awarded opportunity with a real PO,
                      so the server returns no total rather than a $0. */}
                  {data.in_scope === false ? (
                    <strong style={{ color: colors.charcoal }}>No RentalMan figure for this opportunity.</strong>
                  ) : (
                    <>
                      <strong style={{ color: colors.charcoal, fontSize: 16 }}>{money2(data.total)}</strong>
                      {' ex-tax · '}{invoiceCount} invoice{invoiceCount === 1 ? '' : 's'} counted
                    </>
                  )}
                  {heldCount > 0 && (
                    <span style={{ color: '#B45309' }}>
                      {' · '}{heldCount} held out ({money2(data.held_out_amount)})
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                  RentalMan data pulled {data.pulled_at ? fmtDateTime(data.pulled_at) : 'not yet'}
                  {data.loaded_at ? ` · loaded into CRM ${fmtDateTime(data.loaded_at)}` : ''}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                  PO on the opportunity:{' '}
                  {poKeys.length
                    ? <strong style={{ color: colors.charcoal, fontFamily: 'monospace' }}>{poKeys.join(', ')}</strong>
                    : 'none'}
                  {placeholderKeys.length > 0 && (
                    <span title="Placeholder PO values such as TBD are never matched to invoices: company-wide they appear on other customers' invoices.">
                      {' · '}<span style={{ fontFamily: 'monospace' }}>{placeholderKeys.join(', ')}</span> not matched (placeholder)
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: colors.textMuted, lineHeight: 1 }}
            aria-label="Close"
          >×</button>
        </div>

        <div style={{ padding: '12px 22px 22px', ...(isMobile ? { overflowX: 'auto' } : { overflow: 'auto', flex: 1 }) }}>
          {error && <div style={styles.errorBox}>{error}</div>}
          {!data && !error && <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>Loading invoices…</div>}

          {data && (
            <>
              {(heldCount > 0 || multiPo > 0 || suffixed > 0 || customers.size > 1 || collapsedCount > 0) && (
                <div style={{ fontSize: 12, color: colors.charcoalSoft, background: colors.graySoft, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, lineHeight: 1.5 }}>
                  {heldCount > 0 && (
                    <div style={{ color: '#92400E' }}>
                      {heldCount} invoice{heldCount === 1 ? '' : 's'} name{heldCount === 1 ? 's' : ''} this PO together with another opportunity, so {heldCount === 1 ? 'it counts' : 'they count'} toward neither.
                    </div>
                  )}
                  {multiPo > 0 && (
                    <div>
                      {multiPo} counted invoice{multiPo === 1 ? '' : 's'} list{multiPo === 1 ? 's' : ''} more than one PO. No other awarded opportunity carries the other PO, so {multiPo === 1 ? 'it counts' : 'they count'} here.
                    </div>
                  )}
                  {suffixed > 0 && (
                    <div>
                      {suffixed} counted invoice{suffixed === 1 ? ' carries' : 's carry'} this PO with a RentalMan suffix (for example 84127538-0).
                    </div>
                  )}
                  {customers.size > 1 && (
                    <div>Billed under {customers.size} RentalMan customers.</div>
                  )}
                  {collapsedCount > 0 && (
                    <div>
                      {collapsedCount} duplicate listing{collapsedCount === 1 ? '' : 's'} (the same invoice under a second RentalMan account) counted once.
                    </div>
                  )}
                </div>
              )}

              {rows.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
                  No RentalMan invoices yet.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ ...styles.table, fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={th}>Invoice</th>
                        <th style={th}>Date</th>
                        <th style={th}>Status</th>
                        <th style={th}>RM Branch</th>
                        <th style={th}>Account</th>
                        <th style={th}>Customer</th>
                        <th style={th}>PO</th>
                        <th style={{ ...th, textAlign: 'right' }}>Ex-tax</th>
                        <th style={th}>Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(inv => {
                        const held = !!inv.held_reason;
                        const dim = held || inv.collapsed;
                        return (
                          <tr key={`${inv.account_no}|${inv.invoice_no}|${inv.invoice_seq}`}
                            style={held ? { background: '#FFFBEB' } : undefined}>
                            <td style={{ ...td, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{inv.invoice_no}-{inv.invoice_seq}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(inv.invoice_date)}</td>
                            <td style={td}>{inv.invoice_status || ''}</td>
                            <td style={td}>{inv.branch_location || ''}</td>
                            <td style={{ ...td, color: colors.textMuted }}>{inv.account_no || ''}</td>
                            <td style={td}>{inv.customer_name || ''}</td>
                            <td style={{ ...td, fontFamily: 'monospace' }}>{inv.po_number || ''}</td>
                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600,
                              color: dim ? colors.gray : colors.text,
                              textDecoration: inv.collapsed ? 'line-through' : 'none' }}>
                              {money2(inv.amount_ex_tax)}
                            </td>
                            <td style={{ ...td, color: held ? '#B45309' : colors.charcoal, fontWeight: held ? 700 : 500 }}>
                              {matchLabel(inv)}
                              {inv.collapsed && (
                                <div style={{ fontSize: 10, color: colors.textMuted, fontWeight: 400 }}
                                  title="The same invoice is listed under a second RentalMan account (bill-to and job account). It is taken once.">
                                  {held ? 'duplicate listing' : 'duplicate listing, counted once'}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ ...td, fontWeight: 700 }} colSpan={7}>Counted total</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{money2(data.total)}</td>
                        <td style={td}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
